import {openBankingRequest, request} from '../services/request';
import {openBankingEndpoints, backendEndpoints} from '../../api';
import {openBankingConstants} from '../constants';
import {apiHelpers, getObPaymentMethodOptionLabel} from '../../utils/helpers';

const getUrl = apiHelpers.getUrl;

const errorHandlerProps = {disableAlert: true};

// Maps a nested OB payment_method object to a filter option
// ({value: ob_payment_method_id, label: "<last4 iban> <bank_name>"}), or null
// when there is no payment method (some access-list entries have it as null).
// `iban`/`bank_name` are kept on the option so the reducer can re-sync the label
// against account renames (the label is only a snapshot at build time).
const toObPaymentMethodOption = (pm) =>
  pm?.ob_payment_method_id
    ? {
        account_id: pm.account_id,
        value: pm.ob_payment_method_id,
        iban: pm.iban,
        bank_name: pm.bank_name ?? '',
        label: getObPaymentMethodOptionLabel(pm.iban, pm.bank_name),
      }
    : null;

const dedupeByValue = (options) => [...new Map(options.map((o) => [o.value, o])).values()];

// A cursor-paginated walk started for one company must not write a later page's
// results after the user has switched companies mid-walk — the walk can otherwise
// keep running (and, since the request headers are attached from localStorage at
// send time, keep hitting the backend) under the new company while still holding a
// cursor issued for the old one (H1MVP-5024). Callers anchor on the company id active
// when the walk began and recheck it here before every dispatch.
const isStillActiveCompany = (getState, companyId) => getState().company?.id === companyId;

// Builds the query for a paged OB list request: keeps the cursor plus any
// non-empty array filters (e.g. account_ids), and returns undefined when there
// is nothing to send so the request omits the query string entirely.
const buildListQuery = ({cursor, ...filters}) => {
  const query = cursor ? {cursor} : {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value?.length) query[key] = value;
  });
  return Object.keys(query).length ? query : undefined;
};

const getAccounts = ({cursor, accountIds, companyId, successCallback, errorCallback} = {}) => {
  return (dispatch, getState) => {
    const startCompanyId = companyId ?? getState().company?.id;
    dispatch({type: openBankingConstants.SET_ACCOUNTS_LOADING, isLoading: true});
    openBankingRequest.get({
      dispatch,
      url: openBankingEndpoints.GET_OPEN_BANKING_ACCOUNTS,
      query: buildListQuery({cursor, account_ids: accountIds}),
      successCallback: (data) => {
        // The company may have changed while this page was in flight — drop it rather
        // than write another company's accounts into the current company's state.
        if (!isStillActiveCompany(getState, startCompanyId)) return;
        const results = data?.results ?? [];
        dispatch({
          type: cursor ? openBankingConstants.APPEND_ACCOUNTS : openBankingConstants.GET_ACCOUNTS,
          data: results,
        });
        // Merge this page's OB payment method options into the store; the reducer
        // unions and dedupes atomically so pages/access-list entries accumulate.
        const pageOptions = results.map((a) => toObPaymentMethodOption(a?.payment_method)).filter(Boolean);
        if (pageOptions.length) {
          dispatch({type: openBankingConstants.APPEND_OB_PAYMENT_METHODS, data: pageOptions});
        }
        successCallback?.(data, startCompanyId);
      },
      errorCallback: (err) => {
        if (!isStillActiveCompany(getState, startCompanyId)) return;
        dispatch({type: openBankingConstants.SET_ACCOUNTS_ERROR, data: err});
        errorCallback?.(err);
      },
      errorHandlerProps,
    });
  };
};

const getAccountTransactions = ({accountId, query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.get({
      dispatch,
      url: getUrl(openBankingEndpoints.GET_OPEN_BANKING_ACCOUNT_TRANSACTIONS, {account_id: accountId}),
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const getTransactions = ({query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.get({
      dispatch,
      url: openBankingEndpoints.GET_OPEN_BANKING_TRANSACTIONS,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const getCashflowMonthly = ({query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.get({
      dispatch,
      url: openBankingEndpoints.GET_OPEN_BANKING_CASHFLOW_MONTHLY,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const getTransaction = ({transactionId, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.get({
      dispatch,
      url: getUrl(openBankingEndpoints.GET_OPEN_BANKING_TRANSACTION, {transaction_id: transactionId}),
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const getConnection = ({connectionId, successCallback, errorCallback} = {}) => {
  return (dispatch, getState) => {
    openBankingRequest.get({
      dispatch,
      url: getUrl(openBankingEndpoints.GET_OPEN_BANKING_CONNECTION, {connection_id: connectionId}),
      successCallback: (data) => {
        const {connections = [], accounts = []} = getState().openBanking ?? {};

        // Add the new connection to the list (or update it in place if it is already
        // there — e.g. it was added at connect-success before its accounts synced).
        const isNew = !connections.some((c) => c.id === connectionId);
        dispatch({type: isNew ? openBankingConstants.ADD_CONNECTION : openBankingConstants.UPDATE_CONNECTION, data});

        // A new connection's accounts sync asynchronously and often only arrive on a
        // later (no-longer-"new") getConnection via the CONNECTION_ACTIVE websocket, so
        // append/grant by presence rather than isNew. Append any accounts not yet in the
        // store and grant them in the scoped access list so subsequent scoped loads keep
        // them instead of dropping them on the next GET_ACCOUNTS replace (H1MVP-4452).
        const knownAccountIds = new Set(accounts.map((a) => a.id));
        const incomingAccounts = data.accounts ?? [];
        const newAccounts = incomingAccounts.filter((a) => a.id && !knownAccountIds.has(a.id));
        const updatedAccounts = incomingAccounts.filter((a) => a.id && knownAccountIds.has(a.id));
        if (newAccounts.length) {
          dispatch({type: openBankingConstants.APPEND_ACCOUNTS, data: newAccounts});
          dispatch(addAccountAccessIds(newAccounts.map((a) => a.id)));
        }
        // Existing accounts on an already-known connection may have fresh fields
        // (e.g. balance) after a refresh/reconnect — update them in place, keeping
        // their position in the accounts list.
        if (updatedAccounts.length) {
          dispatch({type: openBankingConstants.UPDATE_ACCOUNTS, data: updatedAccounts});
        }
        successCallback?.(data);
      },
      errorCallback,
      errorHandlerProps,
    });
  };
};

const getConnections = ({cursor, connectionIds, companyId, successCallback, errorCallback} = {}) => {
  return (dispatch, getState) => {
    const startCompanyId = companyId ?? getState().company?.id;
    dispatch({type: openBankingConstants.SET_CONNECTIONS_LOADING, isLoading: true});
    openBankingRequest.get({
      dispatch,
      url: openBankingEndpoints.GET_OPEN_BANKING_CONNECTIONS,
      query: buildListQuery({cursor, connection_ids: connectionIds}),
      successCallback: (data) => {
        if (!isStillActiveCompany(getState, startCompanyId)) return;
        dispatch({
          type: cursor ? openBankingConstants.APPEND_CONNECTIONS : openBankingConstants.GET_CONNECTIONS,
          data: data?.results ?? [],
        });
        successCallback?.(data, startCompanyId);
      },
      errorCallback: (err) => {
        if (!isStillActiveCompany(getState, startCompanyId)) return;
        dispatch({type: openBankingConstants.SET_CONNECTIONS_ERROR, data: err});
        errorCallback?.(err);
      },
      errorHandlerProps,
    });
  };
};

const updateAccount = ({accountId, data, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.patch({
      dispatch,
      url: getUrl(openBankingEndpoints.PATCH_OPEN_BANKING_ACCOUNT, {account_id: accountId}),
      data,
      successCallback: (response) => {
        dispatch({type: openBankingConstants.UPDATE_ACCOUNT, data: response});
        successCallback?.(response);
      },
      errorCallback,
      errorHandlerProps,
    });
  };
};

const refreshConnection = ({connectionId, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.CLOSE_CONNECT_MODAL});
    openBankingRequest.post({
      dispatch,
      url: getUrl(openBankingEndpoints.POST_OPEN_BANKING_CONNECTION_REFRESH, {connection_id: connectionId}),
      successCallback: (data) => {
        dispatch({type: openBankingConstants.CREATE_CONNECTION, data});
        successCallback?.(data);
      },
      errorCallback,
      errorHandlerProps,
    });
  };
};

const createConnection = ({successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.SET_CONNECTION_CREATING});
    openBankingRequest.post({
      dispatch,
      url: openBankingEndpoints.POST_OPEN_BANKING_CONNECTIONS,
      successCallback: (data) => {
        dispatch({type: openBankingConstants.CREATE_CONNECTION, data});
        successCallback?.(data);
      },
      errorCallback: (err) => {
        dispatch({type: openBankingConstants.CREATE_CONNECTION_ERROR, data: err?.message ?? err});
        errorCallback?.(err);
      },
      errorHandlerProps,
    });
  };
};

const reconnectConnection = ({connectionId, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.CLOSE_CONNECT_MODAL});
    openBankingRequest.post({
      dispatch,
      url: getUrl(openBankingEndpoints.POST_OPEN_BANKING_CONNECTION_RECONNECT, {connection_id: connectionId}),
      successCallback: (data) => {
        dispatch({type: openBankingConstants.CREATE_CONNECTION, data});
        successCallback?.(data);
      },
      errorCallback,
      errorHandlerProps,
    });
  };
};

const revokeConnection = ({connectionId, consentId, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.put({
      dispatch,
      url: getUrl(openBankingEndpoints.PUT_OPEN_BANKING_CONNECTION_CONSENT_REVOKE, {connection_id: connectionId, consent_id: consentId}),
      successCallback: (response) => {
        dispatch({type: openBankingConstants.UPDATE_CONNECTION_STATUS, data: response});
        successCallback?.();
      },
      errorCallback,
      errorHandlerProps,
    });
  };
};

const downloadAccountTransactions = ({accountId, headers, query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    if (!accountId) {
      errorCallback?.();
      return;
    }
    openBankingRequest.get({
      dispatch,
      headers,
      url: getUrl(openBankingEndpoints.GET_OPEN_BANKING_ACCOUNT_TRANSACTIONS_DOWNLOAD, {account_id: accountId}),
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const downloadAllTransactions = ({headers, query, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    openBankingRequest.get({
      dispatch,
      headers,
      url: openBankingEndpoints.GET_OPEN_BANKING_TRANSACTIONS_DOWNLOAD,
      query,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const closeConnectModal = (errorMessage) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.CLOSE_CONNECT_MODAL, error: errorMessage ?? null});
  };
};

// Account access is owned by the main backend service (not the OB microservice),
// so these two actions use `request`/`backendEndpoints` instead of `openBankingRequest`.
const getAccountAccess = ({accountId, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    request.get({
      dispatch,
      url: getUrl(backendEndpoints.GET_OB_ACCOUNT_ACCESS, {account_id: accountId}),
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

// Walks every page of /ob-account/access, accumulating the ids of the accounts
// the current user may access plus the OB payment method options, then stores
// both in one dispatch. `scoped` controls whether the collected ids are used to
// scope account/connection loading — admins pass scoped=false so their loads
// stay unfiltered while still populating the payment method options.
const getAccountAccessList = ({scoped = true, successCallback, errorCallback} = {}) => {
  return (dispatch, getState) => {
    const startCompanyId = getState().company?.id;
    const accountIds = [];
    const paymentMethodOptions = [];

    const loadPage = (cursor) => {
      request.get({
        dispatch,
        url: backendEndpoints.GET_OB_ACCOUNT_ACCESS_LIST,
        query: cursor ? {cursor} : undefined,
        successCallback: (data) => {
          // The company may have changed while this walk was still paging — drop it
          // rather than let a page issued for the old company overwrite the new
          // company's (already-cleared) access list.
          if (!isStillActiveCompany(getState, startCompanyId)) return;

          const results = data?.results ?? [];
          accountIds.push(...results.map((r) => r?.account_id).filter(Boolean));
          paymentMethodOptions.push(...results.map((r) => toObPaymentMethodOption(r?.payment_method)).filter(Boolean));

          const {next_page: nextPage, cursor: nextCursor} = data?.pagination ?? {};
          if (nextPage) {
            loadPage(nextCursor);
            return;
          }

          dispatch({type: openBankingConstants.SET_ACCOUNT_ACCESS, data: scoped ? accountIds : []});
          dispatch({type: openBankingConstants.SET_OB_PAYMENT_METHODS, data: dedupeByValue(paymentMethodOptions)});
          // An empty scoped access list means there are no accounts to fetch, so settle
          // the accounts-loaded flag here — otherwise the page loader would spin forever.
          if (scoped && accountIds.length === 0) {
            dispatch(setAccountsLoaded(true));
          }
          successCallback?.(data);
        },
        errorCallback: (err) => {
          if (!isStillActiveCompany(getState, startCompanyId)) return;
          dispatch({type: openBankingConstants.SET_ACCOUNT_ACCESS, data: []});
          dispatch({type: openBankingConstants.SET_OB_PAYMENT_METHODS, data: []});
          // Settle the flag on failure too, so a failed access-list fetch shows the empty
          // state rather than an infinite loader.
          dispatch(setAccountsLoaded(true));
          errorCallback?.(err);
        },
        errorHandlerProps,
      });
    };

    loadPage();
  };
};

const setAccountAccess = () => ({type: openBankingConstants.SET_ACCOUNT_ACCESS, data: []});

const setAccountsLoaded = (isLoaded = true) => ({type: openBankingConstants.SET_ACCOUNTS_LOADED, isLoaded});

// Merge newly-granted account ids (e.g. a just-created connection's accounts)
// into the scoped access list so subsequent scoped loads include them (H1MVP-4452).
const addAccountAccessIds = (accountIds = []) => ({
  type: openBankingConstants.ADD_ACCOUNT_ACCESS,
  data: accountIds,
});

const setObPaymentMethods = () => ({type: openBankingConstants.SET_OB_PAYMENT_METHODS, data: []});

const updateAccountAccess = ({accountId, data, successCallback, errorCallback} = {}) => {
  return (dispatch) => {
    request.put({
      dispatch,
      url: getUrl(backendEndpoints.PUT_OB_ACCOUNT_ACCESS, {account_id: accountId}),
      data,
      successCallback,
      errorCallback,
      errorHandlerProps,
    });
  };
};

const markAccountsRefreshed = ({connectionId} = {}) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.ACCOUNTS_REFRESH_DONE, connectionId, at: Date.now()});
  };
};

const setAccount = (account) => ({
  type: openBankingConstants.UPDATE_ACCOUNT,
  data: account,
});

const setFirstNewAccountId = (id) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.SET_FIRST_NEW_ACCOUNT_ID, id});
  };
};

const clearFirstNewAccountId = () => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.CLEAR_FIRST_NEW_ACCOUNT_ID});
  };
};

// Remembers which connection/account a manual refresh was requested for, so the
// TransactionsPage websocket handler can tell a later `connectionActive` event
// apart from unrelated ones and reload only that account's transactions.
const setPendingAccountRefresh = ({connectionId, accountId} = {}) => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.SET_PENDING_ACCOUNT_REFRESH, connectionId, accountId});
  };
};

const clearPendingAccountRefresh = () => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.CLEAR_PENDING_ACCOUNT_REFRESH});
  };
};

const clearOpenBankingData = () => {
  return (dispatch) => {
    dispatch({type: openBankingConstants.CLEAR_OPEN_BANKING_DATA});
  };
};

export const openBankingActions = {
  getAccounts,
  getCashflowMonthly,
  getAccountTransactions,
  getTransactions,
  getTransaction,
  downloadAccountTransactions,
  downloadAllTransactions,
  getConnection,
  getConnections,
  updateAccount,
  refreshConnection,
  createConnection,
  reconnectConnection,
  revokeConnection,
  closeConnectModal,
  getAccountAccess,
  getAccountAccessList,
  setAccountAccess,
  setAccountsLoaded,
  addAccountAccessIds,
  setObPaymentMethods,
  updateAccountAccess,
  markAccountsRefreshed,
  setAccount,
  setFirstNewAccountId,
  clearFirstNewAccountId,
  setPendingAccountRefresh,
  clearPendingAccountRefresh,
  clearOpenBankingData,
};
